import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import type { Id, Doc } from "../../../../convex/_generated/dataModel";
import { LoadingSpinner } from "../../../shared/ui/components/LoadingSpinner";
import {
  ArrowLeftIcon,
  PhotoIcon,
  SwatchIcon,
  DocumentTextIcon,
  BeakerIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  TrashIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { toast } from "sonner";
import { ReferenceCard } from "../../../shared/ui/components/ReferenceCard";

type TabType = "references" | "themes" | "decisions" | "styleguide" | "chat" | "settings";

type ReferenceDoc = Doc<'references'> & {
  imageUrl?: string | null;
  sourceURL?: string | null;
  tags: Doc<'tags'>[];
  mediaItems: (Doc<'mediaItems'> & {
    url: string | null;
    mediaType: 'image' | 'video';
  })[];
  app?: Doc<'apps'> | null;
};

export function ProjectWorkspacePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("references");

  const project = useQuery(
    api.projects.getProject,
    projectId ? { projectId: projectId as Id<"projects"> } : "skip"
  );

  const projectReferences = useQuery(
    api.projects.getProjectReferences,
    projectId ? { projectId: projectId as Id<"projects"> } : "skip"
  );

  const removeReferenceFromProject = useMutation(api.projects.removeReferenceFromProject);
  const deleteReference = useMutation(api.references.deleteReference);
  const [deletingReference, setDeletingReference] = useState<ReferenceDoc | null>(null);

  if (!projectId) {
    return <div>Invalid project ID</div>;
  }

  const handleRemoveReference = async (referenceId: Id<"references">) => {
    try {
      await removeReferenceFromProject({
        projectId: projectId as Id<"projects">,
        referenceId,
      });
      toast.success("Reference removed from project");
    } catch (error) {
      console.error("Error removing reference:", error);
      toast.error("Failed to remove reference");
    }
  };

  const handleEdit = (reference: ReferenceDoc) => {
    // Navigate to the reference detail page
    navigate(`/reference/${reference._id}`);
  };

  const handleDelete = (reference: ReferenceDoc) => {
    setDeletingReference(reference);
  };

  const confirmDelete = async () => {
    if (!deletingReference) return;
    
    try {
      await deleteReference({ id: deletingReference._id });
      toast.success("Reference deleted successfully!");
      setDeletingReference(null);
    } catch (error) {
      toast.error("Failed to delete reference.");
      console.error(error);
    }
  };

  const cancelDelete = () => {
    setDeletingReference(null);
  };

  const handleAIPrompt = (reference: ReferenceDoc) => {
    // Navigate to search page with AI prompt modal
    navigate(`/search?aiPrompt=${reference._id}`);
  };

  if (project === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (project === null) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Project Not Found</h2>
        <p className="text-gray-600 mb-4">The project you're looking for doesn't exist or you don't have access.</p>
        <button
          onClick={() => navigate("/projects")}
          className="btn px-4 py-2 bg-yellow-300 text-black font-bold uppercase hover:bg-yellow-400 border-yellow-300 hover:border-yellow-400"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Projects
        </button>
      </div>
    );
  }

  const phaseConfig = {
    exploration: { label: "Exploration", color: "bg-yellow-100 text-yellow-800" },
    definition: { label: "Definition", color: "bg-blue-100 text-blue-800" },
    specification: { label: "Specification", color: "bg-purple-100 text-purple-800" },
    documentation: { label: "Documentation", color: "bg-green-100 text-green-800" },
  };

  const tabs = [
    { id: "references" as TabType, label: "References", icon: PhotoIcon, count: project.referenceCount },
    { id: "themes" as TabType, label: "Themes", icon: SwatchIcon, count: project.themeCount },
    { id: "decisions" as TabType, label: "Decisions", icon: DocumentTextIcon, count: project.decisionCount },
    { id: "styleguide" as TabType, label: "Style Guide", icon: BeakerIcon, count: undefined },
    { id: "chat" as TabType, label: "AI Chat", icon: BeakerIcon, count: undefined },
    { id: "settings" as TabType, label: "Settings", icon: Cog6ToothIcon, count: undefined },
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-white border-b-4 border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate("/projects")}
              className="btn btn-ghost btn-sm text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back to Projects
            </button>
            <div className="flex items-center space-x-2">
              <UserGroupIcon className="h-5 w-5 text-gray-400" />
              <span className="text-sm text-gray-600">{project.collaboratorCount} collaborators</span>
            </div>
          </div>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight">{project.name}</h1>
                <span className={`badge badge-lg ${phaseConfig[project.phase].color}`}>
                  {phaseConfig[project.phase].label}
                </span>
              </div>
              {project.description && (
                <p className="text-gray-600 mt-2 max-w-3xl">{project.description}</p>
              )}
              <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
                {project.targetAudience && (
                  <div>
                    <span className="font-medium">Target:</span> {project.targetAudience}
                  </div>
                )}
                {project.brandPersonality && project.brandPersonality.length > 0 && (
                  <div>
                    <span className="font-medium">Personality:</span>{" "}
                    {project.brandPersonality.join(", ")}
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => navigate(`/search?addToProject=${projectId}`)}
              className="btn px-4 py-2 bg-yellow-300 text-black font-bold uppercase hover:bg-yellow-400 border-yellow-300 hover:border-yellow-400"
            >
              <PlusIcon className="h-5 w-5 mr-1" />
              Add References
            </button>
          </div>

          {/* Tabs */}
          <div className="mt-6 border-b-2 border-gray-300">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as TabType)}
                    className={`
                      group inline-flex items-center py-4 px-1 border-b-4 font-bold text-sm transition-colors uppercase tracking-wide
                      ${
                        isActive
                          ? "border-gray-800 text-gray-900"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-400"
                      }
                    `}
                  >
                    <Icon className={`mr-2 h-5 w-5 ${isActive ? "text-gray-900" : "text-gray-400 group-hover:text-gray-500"}`} />
                    {tab.label}
                    {tab.count !== undefined && (
                      <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${isActive ? "bg-gray-200 text-gray-900" : "bg-gray-100 text-gray-600"}`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === "references" && (
          <>
            {projectReferences === undefined ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : projectReferences.length === 0 ? (
              <div className="text-center py-12">
                <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No references yet</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by adding references to your project
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => navigate("/search")}
                    className="btn px-4 py-2 bg-yellow-300 text-black font-bold uppercase hover:bg-yellow-400 border-yellow-300 hover:border-yellow-400"
                  >
                    <PlusIcon className="h-5 w-5 mr-1" />
                    Add Your First Reference
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {projectReferences.map((ref) => (
                  <ReferenceCard
                    key={ref._id}
                    reference={ref}
                    onRemove={handleRemoveReference}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAIPrompt={handleAIPrompt}
                    onTagClick={undefined}
                    viewMode="grid"
                  />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === "themes" && (
          <div className="text-center py-12">
            <SwatchIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No design themes yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Create themes to organize different design directions
            </p>
            <div className="mt-6">
              <button className="btn px-4 py-2 bg-yellow-300 text-black font-bold uppercase hover:bg-yellow-400 border-yellow-300 hover:border-yellow-400">
                Create Theme
              </button>
            </div>
          </div>
        )}

        {activeTab === "decisions" && (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No design decisions yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Document important design decisions and rationale
            </p>
            <div className="mt-6">
              <button className="btn px-4 py-2 bg-yellow-300 text-black font-bold uppercase hover:bg-yellow-400 border-yellow-300 hover:border-yellow-400">
                Document Decision
              </button>
            </div>
          </div>
        )}

        {activeTab === "styleguide" && (
          <div className="text-center py-12">
            <BeakerIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No style guide generated yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Generate a comprehensive style guide from your references
            </p>
            <div className="mt-6">
              <button
                disabled={project.referenceCount === 0}
                className="btn px-4 py-2 bg-yellow-300 text-black font-bold uppercase hover:bg-yellow-400 border-yellow-300 hover:border-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Generate Style Guide
              </button>
              {project.referenceCount === 0 && (
                <p className="mt-2 text-xs text-gray-500">Add references first to generate a style guide</p>
              )}
            </div>
          </div>
        )}

        {activeTab === "chat" && (
          <div className="text-center py-12">
            <BeakerIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">AI Design Exploration</h3>
            <p className="mt-1 text-sm text-gray-500">
              Chat with AI to explore design directions and get recommendations
            </p>
            <div className="mt-6">
              <button className="btn px-4 py-2 bg-yellow-300 text-black font-bold uppercase hover:bg-yellow-400 border-yellow-300 hover:border-yellow-400">
                Start Conversation
              </button>
            </div>
          </div>
        )}

        {activeTab === "settings" && (
          <div className="card bg-white p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Project Settings</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Project Phase</h3>
                <p className="text-sm text-gray-600">
                  Current phase: <span className="font-medium">{phaseConfig[project.phase].label}</span>
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Your Role</h3>
                <p className="text-sm text-gray-600">
                  <span className="font-medium capitalize">{project.userRole}</span>
                </p>
              </div>
              <div className="pt-4 border-t border-gray-200">
                <button className="text-sm text-red-600 hover:text-red-700">
                  Archive Project
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingReference && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="modal-box max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Reference?</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to permanently delete "{deletingReference.title}"? This action cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={cancelDelete}
                className="btn btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="btn btn-error"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
